/**
 * Round 80 — A-C-12 Governance & Explainability
 *
 * Renders a standardized OptiBayExplanation. Pure presentational — no
 * fetches. Optional dev-only "Raw values" panel toggled via
 * `localStorage.optibay_intelligence_debug = '1'`.
 */
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import type { OptiBayExplanation } from '@/lib/intelligence-explanations/types';

const CONFIDENCE_STYLES: Record<OptiBayExplanation['confidence'], string> = {
  low: 'bg-muted text-muted-foreground border-0',
  medium: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-0',
  high: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-0',
};

const CONFIDENCE_RULE: Record<OptiBayExplanation['confidence'], string> = {
  low: 'Limited data — interpret with care.',
  medium: 'Moderate data — directional read.',
  high: 'Sufficient data for a stable read.',
};

function isDebug(): boolean {
  try {
    return typeof window !== 'undefined' && window.localStorage?.getItem('optibay_intelligence_debug') === '1';
  } catch {
    return false;
  }
}

export function ExplanationPopover({ explanation }: { explanation: OptiBayExplanation }) {
  const debug = isDebug();
  return (
    <div className="p-3 space-y-3 text-sm">
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium leading-snug">{explanation.summary}</p>
        <Badge className={`${CONFIDENCE_STYLES[explanation.confidence]} text-xs capitalize shrink-0`}>
          {explanation.confidence}
        </Badge>
      </div>

      {explanation.signals.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
            Signals used
          </p>
          <ul className="space-y-0.5">
            {explanation.signals.map((s, i) => (
              <li key={i} className="text-xs flex gap-1.5">
                <span className="text-muted-foreground">·</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {explanation.data_points.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
            Data points
          </p>
          <ul className="space-y-0.5">
            {explanation.data_points.map((d, i) => (
              <li key={i} className="text-xs text-muted-foreground">
                {d}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="text-xs text-muted-foreground border-t pt-2">
        <p>
          <span className="font-medium text-foreground capitalize">{explanation.confidence} confidence:</span>{' '}
          {CONFIDENCE_RULE[explanation.confidence]}
        </p>
        {explanation.last_updated_at && (
          <p className="mt-1">
            Last updated{' '}
            {formatDistanceToNow(new Date(explanation.last_updated_at), { addSuffix: true })}
          </p>
        )}
      </div>

      {debug && explanation.debug && (
        <details className="text-[10px] border rounded p-2 bg-muted/40">
          <summary className="cursor-pointer font-mono">raw values</summary>
          <pre className="overflow-auto mt-1">{JSON.stringify(explanation.debug, null, 2)}</pre>
        </details>
      )}

      <div className="border-t pt-2">
        <Link
          to="/enterprise/intelligence/governance"
          className="text-xs text-primary hover:underline"
        >
          View methodology →
        </Link>
      </div>
    </div>
  );
}
